import { defineStore, acceptHMRUpdate } from 'pinia';
import { useLoadingStore } from '@/stores/loading';

import type { BootstrapResponse } from '@/types/api/bootstrap';

export const useAppStore = defineStore('app', () => {
  /********************
   * REFS & VARS       *
   ********************/
  const clickContext = ref<string | null>(null);
  const cookies = ref(['required', 'optional']);
  const floorAdActive = ref(false);
  const floorAdClosed = ref(false);
  const isInitialRequest = ref(true);
  const permissions = shallowRef<BootstrapResponse['permissions']>();
  const routes = shallowRef<BootstrapResponse['routes']>([]);
  const settings = shallowRef<BootstrapResponse['settings']>();
  const sidebarOpen = ref(false);
  const userMenuOpen = ref(false);
  const backendUnavailable = ref(false);
  const showSocialLogin = ref(true);

  /********************
   * COMPOSITIONS      *
   ********************/
  const bootstrapService = useBootstrapService();
  const seoStore = useSeoStore();
  const spService = useSpService();
  const { setStatusError, setStatusLoading, setStatusSuccess } = useLoadingStore();

  /********************
   * FUNCTIONS         *
   ********************/
  async function getBootstrap({ lang }: { lang: string }) {
    setStatusLoading('getBootstrap');

    try {
      const response = await bootstrapService.getBootstrap(lang);

      const mappedRoutes = response.routes.map((routeEntries) => {
        return routeEntries.map((entry) => {
          return { ...entry, url: `/${entry.lang}${entry.url}` };
        });
      });

      permissions.value ??= response?.permissions;
      routes.value = response?.routes ? mappedRoutes : routes.value;
      settings.value ??= response?.settings;

      if (response?.seo) {
        seoStore.seo = { ...response.seo, ...(response.num_campsites && { num_campsites: response.num_campsites }) };
        seoStore.setDynamicMetaParam(toRaw(seoStore.seo));
      }

      setStatusSuccess('getBootstrap');
      return response;
    } catch (error) {
      setStatusError({ name: 'getBootstrap', error });
      console.error(error);
      throw error;
    }
  }

  async function ti({ slug, lang, ti, te }: { slug: string; lang: string; ti: string; te: string }) {
    try {
      return await spService.ti(lang, { slug, ti, te });
    } catch (_e) {
      return null;
    }
  }

  return {
    backendUnavailable,
    clickContext,
    cookies,
    floorAdActive,
    floorAdClosed,
    getBootstrap,
    isInitialRequest,
    permissions,
    routes,
    settings,
    showSocialLogin,
    sidebarOpen,
    ti,
    userMenuOpen,
  };
});

export type AppStore = ReturnType<typeof useAppStore>;

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAppStore, import.meta.hot));
}
